import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { postTaricDetails, putTaricUpdate } from '../../api/taric';
import { Button } from '../../components/Buttons';
import ContentPanel from '../../components/ContentPanel';
import { RhfTextField, RhfSelect } from '../../components/Inputs';
import { API_CONFIG, cleanSavePayload, extractDataFromApi, getDefaultFormValues } from './taricDetailHelpers';

const fieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: 'common.white',
  },
};

const tableBorderSx = {
  overflowX: 'auto',
  border: '1px solid #dee2e6',
  borderRadius: '4px',
};

const cellHeaderSx = (isLast) => ({
  bgcolor: '#f8f9fa',
  color: '#495057',
  fontWeight: 600,
  fontSize: '0.85rem',
  borderBottom: '2px solid #dee2e6',
  borderRight: isLast ? 'none' : '1px solid #dee2e6',
  py: 1,
  px: 1.5,
});

const cellBodySx = (isLast) => ({
  borderBottom: '1px solid #dee2e6',
  borderRight: isLast ? 'none' : '1px solid #dee2e6',
  py: 0.75,
  px: 1,
});

const vatOptions = [
  { label: 'VATZ', value: 'VATZ' },
  { label: 'VATS', value: 'VATS' },
  { label: 'VATS/VATZ', value: 'VATS/VATZ' },
];

const DOC_CODE_COLUMNS = [
  { key: 'no', label: 'No.', width: 64, align: 'center' },
  { key: 'code', label: 'Code', width: '25%' },
  { key: 'description', label: 'Description' },
];

const DocumentCodesTable = ({ control, isImport }) => {
  const config = isImport ? API_CONFIG.import : API_CONFIG.export;
  const tableId = isImport ? 'import-doc-codes' : 'export-doc-codes';

  return (
    <Table
      size="small"
      sx={{
        bgcolor: 'common.white',
        '& tbody tr, & tbody tr > *': {
          bgcolor: 'common.white',
        },
      }}
    >
      <TableHead>
        <TableRow>
          {DOC_CODE_COLUMNS.map((column, index) => (
            <TableCell
              key={column.key}
              width={column.width}
              align={column.align}
              sx={cellHeaderSx(index === DOC_CODE_COLUMNS.length - 1)}
              data-testid={`${tableId}-header-${column.key}`}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {config.docCodes.map((codeKey, index) => {
          const rowNum = index + 1;
          const descKey = config.descriptions[index];
          const isLastRow = index === config.docCodes.length - 1;
          const borderBottom = isLastRow ? 'none' : '1px solid #dee2e6';

          const cellContent = {
            no: <Typography variant="body2">{rowNum}</Typography>,
            code: <RhfTextField name={codeKey} control={control} size="small" sx={fieldSx} />,
            description: <RhfTextField name={descKey} control={control} size="small" sx={fieldSx} />,
          };

          return (
            <TableRow key={codeKey} data-testid={`${tableId}-row-${rowNum}`}>
              {DOC_CODE_COLUMNS.map((column, colIndex) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    ...cellBodySx(colIndex === DOC_CODE_COLUMNS.length - 1),
                    borderBottom,
                    ...(column.key === 'no' ? { color: '#495057' } : null),
                  }}
                  data-testid={`${tableId}-row-${rowNum}-${column.key}`}
                >
                  {cellContent[column.key]}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const AdditionalCodesTable = ({ control, fieldNames, tableId = 'additional-codes' }) => (
  <Table
    size="small"
    sx={{
      bgcolor: 'common.white',
      '& tbody tr, & tbody tr > *': {
        bgcolor: 'common.white',
      },
    }}
  >
    <TableBody>
      {fieldNames.map((name, index) => {
        const isLastRow = index === fieldNames.length - 1;
        const isVat = name === 'VAT';

        return (
          <TableRow key={name} data-testid={`${tableId}-row-${name}`}>
            <TableCell
              sx={{ ...cellBodySx(true), borderBottom: isLastRow ? 'none' : '1px solid #dee2e6' }}
              data-testid={`${tableId}-cell-${name}`}
            >
              {isVat ? (
                <RhfSelect name={name} control={control} size="small" options={vatOptions} sx={fieldSx} />
              ) : (
                <RhfTextField name={name} control={control} size="small" sx={fieldSx} />
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const TaricToolDetail = () => {
  const { commodityCode } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: getDefaultFormValues(commodityCode),
  });

  const commcode = watch('commcode');
  const expCommodityCode = watch('expCommodityCode');

  const { data, error, isFetching } = useQuery({
    queryKey: ['taricDetails', commodityCode],
    queryFn: () => postTaricDetails(commodityCode),
    enabled: !!commodityCode,
  });

  const saveDetails = useMutation({
    mutationFn: (formData) => putTaricUpdate(cleanSavePayload(formData)),
    onSuccess: () => {
      setToast({
        open: true,
        severity: 'success',
        message: 'Taric details have been saved successfully.',
      });
    },
    onError: () => {
      setToast({
        open: true,
        severity: 'error',
        message: 'Failed to save changes. Please try again.',
      });
    },
  });

  useEffect(() => {
    if (!data) return;

    const importData = extractDataFromApi(data.importDetails, commodityCode, true);
    const exportData = extractDataFromApi(data.exportDetails, commodityCode, false);
    reset({ ...importData, ...exportData });
  }, [data, commodityCode, reset]);

  useEffect(() => {
    if (!error) return;

    setToast({
      open: true,
      severity: 'error',
      message: 'Failed to load commodity code data. Please try again.',
    });
  }, [error]);

  const loading = isFetching;
  const saving = saveDetails.isPending;

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className="full-width-container">
      <ContentPanel maxWidth={1200} sx={{ my: '2rem' }}>
        <Typography variant="h4" sx={{ color: '#033249', mb: 2, fontWeight: 500 }}>
          TARIC Tool
        </Typography>

        <Button
          variant="text"
          startIcon={<ArrowBackIcon sx={{ color: '#000000' }} />}
          onClick={() => navigate('/taric-tool')}
          sx={{
            mb: 2,
            px: 0,
            py: 0.5,
            lineHeight: 1.25,
            color: '#000000',
            textDecoration: 'underline',
            '&:hover': { color: '#000000', textDecoration: 'underline' },
          }}
        >
          Back to Taric Tool
        </Button>

        <Box
          component="form"
          onSubmit={handleSubmit((formData) => saveDetails.mutate(formData))}
          noValidate
          sx={{
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? 'none' : 'auto',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              columnGap: 3,
              mt: 2,
            }}
          >
            <Box
              sx={{
                pr: { md: 3 },
                borderRight: { md: '1px solid #dee2e6' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                IMPORT MEASURES for {commcode || commodityCode} (10)
              </Typography>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Document Codes
              </Typography>
              <Box sx={{ ...tableBorderSx, mb: 3 }}>
                <DocumentCodesTable control={control} isImport />
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Taric Additional Code
              </Typography>
              <Box sx={{ ...tableBorderSx, mb: 3 }}>
                <AdditionalCodesTable
                  control={control}
                  fieldNames={API_CONFIG.import.additionalCodes}
                  tableId="import-additional-codes"
                />
              </Box>
            </Box>

            <Box
              sx={{
                pl: { md: 1 },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                EXPORT MEASURES FOR {expCommodityCode || commodityCode?.substring(0, 8)} (8)
              </Typography>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Documents Codes
              </Typography>
              <Box sx={{ ...tableBorderSx, mb: 3 }}>
                <DocumentCodesTable control={control} isImport={false} />
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Taric Additional Code
              </Typography>
              <Box sx={{ ...tableBorderSx, mb: 3 }}>
                <AdditionalCodesTable
                  control={control}
                  fieldNames={API_CONFIG.export.additionalCodes}
                  tableId="export-additional-codes"
                />
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                VAT Rates
              </Typography>
              <Box sx={{ ...tableBorderSx, mb: 3 }}>
                <AdditionalCodesTable control={control} fieldNames={['VAT']} tableId="vat-rates" />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
              pt: 2.5,
              mt: 2,
              borderTop: '1px solid #dee2e6',
            }}
          >
            <Button
              type="button"
              variant="primary"
              color="secondary"
              disabled={loading || saving}
              onClick={() => navigate(`/taric-tool/${commodityCode}/history`)}
              sx={{ minWidth: 100 }}
            >
              History
            </Button>
            <Button type="submit" variant="primary" disabled={loading || saving} sx={{ minWidth: 100 }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </ContentPanel>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TaricToolDetail;
